import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * <shipping-timeframe> switches its displayed message between a standard and a made-to-order
 * timeframe as the customer changes their option selection, without a page reload. It listens
 * for the theme's `variant:update` event (dispatched by `variant-picker` after each section
 * re-fetch) and checks the newly-selected variant's option values against the comma-separated
 * list in `data-mto-values`.
 *
 * Kept generic on purpose: it doesn't assume the split is driven by a "Gauge" option
 * specifically, so the same component works for any product that sets the
 * custom.ships_standard / custom.ships_made_to_order / custom.made_to_order_values metafields.
 *
 * @extends Component
 */
export default class ShippingTimeframe extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * @param {CustomEvent<{resource: object | null, data: {productId: string}}>} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail?.data?.productId !== this.dataset.productId) return;

    const resource = event.detail.resource;
    const mtoValues = (this.dataset.mtoValues ?? '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    const selectedOptionValues = Array.isArray(resource?.options) ? resource.options : [];
    const isMto =
      mtoValues.length > 0 && selectedOptionValues.some((value) => mtoValues.includes(String(value).trim()));

    const { message, finalSaleNote } = this.refs;
    const text = isMto ? this.dataset.mtoMessage : this.dataset.standardMessage;

    if (message instanceof HTMLElement && text) {
      message.textContent = text;
    }
    if (finalSaleNote instanceof HTMLElement) {
      finalSaleNote.hidden = !isMto;
    }
    this.classList.toggle('zahav-shipping-timeframe--mto', isMto);
  };
}

if (!customElements.get('shipping-timeframe')) {
  customElements.define('shipping-timeframe', ShippingTimeframe);
}
